// Duolingo-style header streak nudge: a Caissa speech-bubble card anchored
// below the header flame, replacing the old plain dark tooltip. Purely
// presentational — Streak/index.tsx owns all the nudge LOGIC (slot
// selection, storage, the ENGAGEMENT_NUDGE_FORCE_SLOT dev flag, and the
// mount/focus/interval triggers) and just hands this component the current
// slot + copy + three callbacks.
import React, {useEffect, useRef} from 'react';
import {Animated, Pressable, StyleSheet, View} from 'react-native';
import {useRive, Layout, Fit, Alignment} from '@rive-app/react-canvas';
import CCText from '@/Atoms/CCText';
import {lbColors, lbRadius, lbShadow} from '@/Screens/Leaderboard/leaderboardTheme';
import caissahello_animation from '@/Assets/Animation/CaissaHello_animation.riv';
import type {NudgeSlot} from '@/Screens/Leaderboard/streakNudges';

interface Props {
  slot: NudgeSlot;
  text: string;
  // Body (non-CTA) tap — opens the existing streak popup, same as tapping
  // the flame chip itself.
  onPressBody: () => void;
  // ✕ corner button — hides the nudge only.
  onDismiss: () => void;
  // "Play a game" pill — navigates to PlayChess and hides the nudge.
  onPlay: () => void;
}

// fresh/midday read as an affirmative/reminder nudge (warm gold); evening
// steps up to orange; lastcall is the "about to break" ⏳ copy (red-ish) —
// mirrors the ⏳ already baked into buildNudgeCopy's lastcall text.
const SLOT_ACCENT: Record<NudgeSlot, string> = {
  fresh: lbColors.gold,
  midday: lbColors.gold,
  evening: lbColors.orange,
  lastcall: lbColors.red,
};

export default function CaissaNudge({
  slot,
  text,
  onPressBody,
  onDismiss,
  onPlay,
}: Props) {
  const accent = SLOT_ACCENT[slot] ?? lbColors.gold;

  // Same Rive wiring as CaissaChatbot's hello animation (proven at this
  // asset/size already) — looped, autoplay, no interaction needed here.
  const {RiveComponent: HelloAnimation} = useRive({
    src: caissahello_animation,
    layout: new Layout({fit: Fit.Cover, alignment: Alignment.TopCenter}),
    autoplay: true,
  });

  // Bounce-in entrance: this component only mounts while the nudge is
  // showing, so mount IS the trigger — no separate "visible" prop needed.
  const enter = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.spring(enter, {
      toValue: 1,
      friction: 7,
      tension: 70,
      useNativeDriver: false,
    }).start();
  }, [enter]);
  const translateY = enter.interpolate({
    inputRange: [0, 1],
    outputRange: [-8, 0],
  });
  const scale = enter.interpolate({inputRange: [0, 1], outputRange: [0.9, 1]});

  return (
    <Animated.View
      style={[styles.wrap, {opacity: enter, transform: [{translateY}, {scale}]}]}
      pointerEvents="box-none">
      <View style={[styles.tail, {borderBottomColor: accent}]} />
      <Pressable onPress={onPressBody} style={[styles.bubble, {borderLeftColor: accent}]}>
        <Pressable
          onPress={e => {
            e?.stopPropagation?.();
            onDismiss();
          }}
          hitSlop={8}
          style={styles.dismiss}>
          <CCText fontSize={12} fontWeight="bold" color={lbColors.muted}>
            ✕
          </CCText>
        </Pressable>

        <View style={styles.row}>
          <View style={styles.avatar}>
            {HelloAnimation ? <HelloAnimation /> : null}
          </View>
          <View style={styles.copyCol}>
            <CCText fontSize={13} fontWeight="bold" color={lbColors.ink}>
              {text}
            </CCText>
            <Pressable
              onPress={e => {
                e?.stopPropagation?.();
                onPlay();
              }}
              style={({pressed}) => [
                styles.playPill,
                pressed && {opacity: 0.85},
              ]}>
              <CCText fontSize={12} fontWeight="bold" color={lbColors.goldInk}>
                Play a game
              </CCText>
              <CCText fontSize={13} fontWeight="bold" color={lbColors.goldInk}>
                →
              </CCText>
            </Pressable>
          </View>
        </View>
      </Pressable>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    position: 'absolute',
    top: '100%',
    right: -6,
    marginTop: 13,
    width: 312,
    zIndex: 50,
  },
  // Small upward-pointing triangle (CSS border trick) connecting the bubble
  // back to the flame above it.
  tail: {
    position: 'absolute',
    top: -8,
    right: 30,
    width: 0,
    height: 0,
    borderLeftWidth: 9,
    borderRightWidth: 9,
    borderBottomWidth: 9,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
  },
  bubble: {
    backgroundColor: lbColors.bg,
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: lbColors.gold,
    borderLeftWidth: 4,
    padding: 14,
    paddingRight: 24,
    ...lbShadow.card,
  },
  dismiss: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  row: {flexDirection: 'row', alignItems: 'flex-start', gap: 10},
  avatar: {
    width: 60,
    height: 60,
    borderRadius: 30,
    overflow: 'hidden',
    backgroundColor: lbColors.goldSoft,
    flexShrink: 0,
  },
  copyCol: {flex: 1, gap: 9, paddingTop: 2, minWidth: 0},
  playPill: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 5,
    backgroundColor: lbColors.gold,
    paddingVertical: 6,
    paddingHorizontal: 13,
    borderRadius: lbRadius.pill,
  },
});
